/**
 * SkillPlacementPickerDialog
 *
 * Unified dialog for placing a suggested skill into the user's packages.
 * Uses Amplify SelectField and app button styles to match the design system.
 */

import React, { useState, useEffect, useRef, useCallback, type FC } from 'react';
import { createPortal } from 'react-dom';
import { SelectField } from '@aws-amplify/ui-react';
import type { PlacementResult, PackageSkillEntry, PackageOption } from '../types/placement';

interface SkillPlacementPickerDialogProps {
  skillName: string;
  placementResult: PlacementResult;
  allPackageSkills: PackageSkillEntry[];
  onPlaceInCategory: (categoryId: string, packageId: string, packageName: string, categoryName: string) => void;
  onCreatePackage: (packageName: string, categoryName: string) => void;
  onDismiss: () => void;
}

function buildPackageOptions(skills: PackageSkillEntry[]): PackageOption[] {
  const pkgMap = new Map<string, PackageOption>();
  for (const skill of skills) {
    if (!pkgMap.has(skill.packageId)) {
      pkgMap.set(skill.packageId, {
        packageId: skill.packageId,
        packageName: skill.packageName,
        source: skill.source,
        categories: [],
      });
    }
    const pkg = pkgMap.get(skill.packageId)!;
    if (!pkg.categories.find(c => c.categoryId === skill.categoryId)) {
      pkg.categories.push({ categoryId: skill.categoryId, categoryName: skill.categoryName });
    }
  }
  return Array.from(pkgMap.values());
}

// Shared input style matching the app's design system
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem 1rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '0.9375rem',
  background: 'white',
  boxSizing: 'border-box',
  color: '#111827',
};

const SkillPlacementPickerDialog: FC<SkillPlacementPickerDialogProps> = ({
  skillName,
  placementResult,
  allPackageSkills,
  onPlaceInCategory,
  onCreatePackage,
  onDismiss,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const [mode, setMode] = useState<'pick' | 'create'>('pick');
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newPackageName, setNewPackageName] = useState('');
  const [newPackageCategoryName, setNewPackageCategoryName] = useState('');

  const packageOptions = buildPackageOptions(allPackageSkills);
  const selectedPkg = packageOptions.find(p => p.packageId === selectedPackageId);
  const categoriesForPkg = selectedPkg?.categories ?? [];
  const isCustomPkg = selectedPkg?.source === 'custom';

  // Reset category when package changes
  useEffect(() => {
    setSelectedCategoryId('');
    setIsCreatingCategory(false);
    setNewCategoryName('');
  }, [selectedPackageId]);

  // Focus management
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = 'unset';
      previousFocusRef.current?.focus();
    };
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onDismiss();
  }, [onDismiss]);

  const handleConfirmPick = () => {
    if (!selectedPackageId) return;

    if (isCreatingCategory) {
      // Creating a new category in an existing custom package
      // We pass a special sentinel so the engine knows to create the category
      onPlaceInCategory(`__new__:${newCategoryName.trim()}`, selectedPackageId, selectedPkg!.packageName, newCategoryName.trim());
    } else {
      if (!selectedCategoryId) return;
      const cat = categoriesForPkg.find(c => c.categoryId === selectedCategoryId);
      if (!cat) return;
      onPlaceInCategory(selectedCategoryId, selectedPackageId, selectedPkg!.packageName, cat.categoryName);
    }
  };

  const handleConfirmCreate = () => {
    if (!newPackageName.trim()) return;
    onCreatePackage(newPackageName.trim(), newPackageCategoryName.trim() || 'General');
  };

  const hasSuggestion = (placementResult.matchType === 'fuzzy' || placementResult.matchType === 'category')
    && placementResult.suggestedCategoryId;

  const canConfirmPick = selectedPackageId && (
    isCreatingCategory ? newCategoryName.trim().length > 0 : selectedCategoryId !== ''
  );

  const dialog = (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000 }}
        onClick={onDismiss}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="picker-title"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10001, background: 'white', borderRadius: '12px',
          padding: '1.5rem', maxWidth: '480px', width: '90%',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          outline: 'none', maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Title */}
        <h2 id="picker-title" style={{ margin: '0 0 0.25rem', fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>
          Where should <span style={{ color: '#2563eb' }}>{skillName}</span> go?
        </h2>
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
          Choose an existing package and category, or create a new one.
        </p>

        {/* Suggestion banner */}
        {hasSuggestion && (
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: '8px', padding: '0.625rem 0.875rem',
            marginBottom: '1rem', fontSize: '0.8rem', color: '#1d4ed8',
            display: 'flex', alignItems: 'center', gap: '0.5rem',
          }}>
            <span>💡</span>
            <span>
              {placementResult.matchType === 'category'
                ? `Suggested: ${placementResult.categoryName} in ${placementResult.packageName}`
                : `Similar skill found in: ${placementResult.categoryName} (${placementResult.packageName})`
              }
            </span>
            <button
              onClick={() => {
                setMode('pick');
                setSelectedPackageId(placementResult.suggestedPackageId!);
                setTimeout(() => setSelectedCategoryId(placementResult.suggestedCategoryId!), 0);
              }}
              style={{
                marginLeft: 'auto', fontSize: '0.75rem', padding: '0.2rem 0.6rem',
                borderRadius: '6px', border: '1px solid #93c5fd',
                background: 'white', color: '#1d4ed8', cursor: 'pointer', fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              Use this
            </button>
          </div>
        )}

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
          {['pick', 'create'].map(m => (
            <button
              key={m}
              onClick={() => setMode(m as 'pick' | 'create')}
              style={{
                flex: 1, padding: '0.5rem', borderRadius: '8px', fontSize: '0.875rem',
                fontWeight: 600, cursor: 'pointer', border: 'none',
                background: mode === m ? '#2563eb' : '#f3f4f6',
                color: mode === m ? 'white' : '#374151',
              }}
            >
              {m === 'pick' ? '📦 Existing package' : '✨ New package'}
            </button>
          ))}
        </div>

        {/* Pick from existing */}
        {mode === 'pick' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {packageOptions.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '0.875rem', textAlign: 'center', padding: '1rem 0' }}>
                No packages yet — create a new one instead.
              </p>
            ) : (
              <>
                {/* Package select */}
                <div>
                  <SelectField
                    label="Package"
                    value={selectedPackageId}
                    onChange={e => setSelectedPackageId(e.target.value)}
                    width="100%"
                  >
                    <option value="" disabled>Select a package...</option>
                    {packageOptions.map(pkg => (
                      <option key={pkg.packageId} value={pkg.packageId}>
                        {pkg.packageName}{pkg.source === 'custom' ? ' (custom)' : ''}
                      </option>
                    ))}
                  </SelectField>
                </div>

                {/* Category select — only shown once a package is selected */}
                {selectedPackageId && (
                  <div>
                    {!isCreatingCategory ? (
                      <>
                        <SelectField
                          label="Category"
                          value={selectedCategoryId}
                          onChange={e => setSelectedCategoryId(e.target.value)}
                          width="100%"
                        >
                          <option value="" disabled>Select a category...</option>
                          {categoriesForPkg.map(cat => (
                            <option key={cat.categoryId} value={cat.categoryId}>
                              {cat.categoryName}
                            </option>
                          ))}
                        </SelectField>

                        {/* Only custom packages can have new categories added */}
                        {isCustomPkg && (
                          <button
                            onClick={() => setIsCreatingCategory(true)}
                            className="btn-base btn-secondary"
                            style={{ marginTop: '0.625rem', fontSize: '0.875rem' }}
                          >
                            + New category
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.375rem' }}>
                          New category name
                        </label>
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={e => setNewCategoryName(e.target.value)}
                          placeholder="e.g. Backend & APIs"
                          autoFocus
                          style={inputStyle}
                        />
                        <button
                          onClick={() => { setIsCreatingCategory(false); setNewCategoryName(''); }}
                          className="btn-base btn-secondary"
                          style={{ marginTop: '0.625rem', fontSize: '0.875rem' }}
                        >
                          ← Back to existing categories
                        </button>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Create new package */}
        {mode === 'create' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.375rem' }}>
                Package name
              </label>
              <input
                type="text"
                value={newPackageName}
                onChange={e => setNewPackageName(e.target.value)}
                placeholder="e.g. My Tech Skills"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.375rem' }}>
                Category name
              </label>
              <input
                type="text"
                value={newPackageCategoryName}
                onChange={e => setNewPackageCategoryName(e.target.value)}
                placeholder="e.g. Backend (defaults to General)"
                style={inputStyle}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
          <button
            onClick={onDismiss}
            style={{
              padding: '0.625rem 1.25rem', borderRadius: '6px',
              border: '1px solid #e0e0e0', background: 'white',
              color: '#374151', fontSize: '0.9375rem', fontWeight: 500, cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
          <button
            onClick={mode === 'pick' ? handleConfirmPick : handleConfirmCreate}
            disabled={mode === 'pick' ? !canConfirmPick : !newPackageName.trim()}
            style={{
              padding: '0.625rem 1.25rem', borderRadius: '6px', border: 'none',
              background: (mode === 'pick' ? !canConfirmPick : !newPackageName.trim()) ? '#93c5fd' : '#2563eb',
              color: 'white', fontSize: '0.9375rem', fontWeight: 500,
              cursor: (mode === 'pick' ? !canConfirmPick : !newPackageName.trim()) ? 'not-allowed' : 'pointer',
            }}
          >
            {mode === 'pick' ? 'Place here' : 'Create & place'}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.getElementById('modal-root') ?? document.body);
};

export default SkillPlacementPickerDialog;
